import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { X, PhoneCall, LayoutDashboard, Mic, ArrowRight } from 'lucide-react';

/**
 * WelcomeModal — shown once per user on first dashboard visit.
 *
 * Persists dismissal in localStorage keyed by user id so it only appears the
 * first time each user logs in. Only rendered for role='manager' (caller
 * controls rendering).
 */
export default function WelcomeModal({ userId }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!userId) return;
    const seen = localStorage.getItem(`hasSeenWelcome_${userId}`);
    if (!seen) setOpen(true);
  }, [userId]);

  const dismiss = () => {
    if (userId) localStorage.setItem(`hasSeenWelcome_${userId}`, 'true');
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm flex items-center justify-center px-4"
      onClick={dismiss}
      data-testid="welcome-modal"
    >
      <div
        className="w-full max-w-lg bg-navy-800 border border-navy-600 rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative px-6 pt-6 pb-4 bg-gradient-to-br from-accent-blue/10 to-transparent">
          <button
            type="button"
            onClick={dismiss}
            aria-label="Lukk"
            className="absolute top-3 right-3 p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-navy-700 transition-colors"
          >
            <X size={18} />
          </button>
          <div className="text-3xl mb-2">🎉</div>
          <h2 className="font-heading text-2xl font-bold text-white tracking-tight">
            Velkommen til AI Resepsjonen!
          </h2>
          <p className="text-slate-400 text-sm mt-2">
            Din AI-resepsjonist er nå klar. Her er hva som skjer:
          </p>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className="flex gap-3 items-start">
            <div className="w-9 h-9 rounded-lg bg-accent-blue/15 flex items-center justify-center flex-shrink-0">
              <PhoneCall size={16} className="text-accent-blue" />
            </div>
            <div className="text-sm text-slate-300">
              Alle innkommende samtaler besvares automatisk — døgnet rundt.
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-9 h-9 rounded-lg bg-accent-green/15 flex items-center justify-center flex-shrink-0">
              <LayoutDashboard size={16} className="text-accent-green" />
            </div>
            <div className="text-sm text-slate-300">
              Du ser oversikt over samtaler og leads her på dashbordet.
            </div>
          </div>
          <div className="flex gap-3 items-start">
            <div className="w-9 h-9 rounded-lg bg-accent-yellow/15 flex items-center justify-center flex-shrink-0">
              <Mic size={16} className="text-accent-yellow" />
            </div>
            <div className="text-sm text-slate-300">
              Du kan tilpasse AI-agenten under <span className="text-white font-medium">AI-agent</span>.
            </div>
          </div>
        </div>

        <div className="px-6 pb-6">
          <button
            type="button"
            onClick={dismiss}
            data-testid="welcome-modal-dismiss"
            className="w-full inline-flex items-center justify-center gap-2 py-3 bg-accent-blue hover:bg-accent-blue-hover text-white font-semibold rounded-lg transition-colors"
          >
            Kom i gang <ArrowRight size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

WelcomeModal.propTypes = {
  userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};
